import { useState } from "react";

import { useToast } from "@/ds";
import { useMediaQuery } from "@/hooks";
import { alterarSituacaoDistribuicao } from "@/modules/financeiro-configuracoes";
import type { DistribuicaoColetor } from "@/modules/financeiro-configuracoes";
import { HUB_PERMISSOES, usePermissoes } from "@/modules/permissoes";
import { useAtivarDireto } from "../hooks/use-ativar-direto";
import { useDistribuicoesDados } from "./hooks/use-distribuicoes-dados";
import {
  DISTRIBUICAO_ATIVADA,
  DISTRIBUICAO_CRIADA,
  DISTRIBUICAO_EDITADA,
  DISTRIBUICAO_INATIVADA,
} from "./distribuicoes.constants";
import { FINANCEIRO_MOBILE_QUERY } from "../financeiro-configuracoes.constants";
import { ATIVAR_ERRO } from "./situacao-distribuicao-dialog/situacao-distribuicao-dialog.constants";
import { montarNoDistribuicao } from "./distribuicoes.utils";
import type {
  AcaoPendente,
  DistribuicoesConteudo,
  DistribuicoesViewModel,
} from "./types-distribuicoes";

const TOAST_POR_ACAO = {
  nova: DISTRIBUICAO_CRIADA,
  editar: DISTRIBUICAO_EDITADA,
  inativar: DISTRIBUICAO_INATIVADA,
} as const;

export function useDistribuicoes(): DistribuicoesViewModel {
  const { hasPermissao } = usePermissoes();
  const toast = useToast();
  const mobile = useMediaQuery(FINANCEIRO_MOBILE_QUERY);
  const podeConsultar = hasPermissao(HUB_PERMISSOES.FINANCEIRO_VIEW_CONFIGURACAO);
  const podeEditar = hasPermissao(HUB_PERMISSOES.FINANCEIRO_EDIT_CONFIGURACAO);
  const [mostrarInativas, setMostrarInativas] = useState(false);
  const [acaoPendente, setAcaoPendente] = useState<AcaoPendente | null>(null);
  const [aplicandoId, setAplicandoId] = useState<string | null>(null);

  const dados = useDistribuicoesDados(
    { incluirInativas: mostrarInativas },
    { enabled: podeConsultar },
  );

  const ativarDireto = useAtivarDireto<DistribuicaoColetor>({
    enviar: (distribuicao, idempotencyKey) =>
      alterarSituacaoDistribuicao(distribuicao.id, "ATIVA", { idempotencyKey }),
    sucesso: () => DISTRIBUICAO_ATIVADA,
    aoConcluir: dados.recarregar,
    erroPadrao: ATIVAR_ERRO,
  });
  const abrirAcao = (acao: AcaoPendente) => {
    if (acao.tipo === "ativar") void ativarDireto(acao.distribuicao);
    else setAcaoPendente(acao);
  };

  const nos = dados.distribuicoes.map((distribuicao) =>
    montarNoDistribuicao(distribuicao, podeEditar ? abrirAcao : null),
  );

  let conteudo: DistribuicoesConteudo = "arvore";
  if (!podeConsultar || dados.semPermissao) conteudo = "sem-permissao";
  else if (dados.erro) conteudo = "erro";
  else if (dados.carregando && nos.length === 0) conteudo = "carregando";
  else if (nos.length === 0) conteudo = "vazio";

  const fecharAcao = () => setAcaoPendente(null);
  const concluirAcao = () => {
    if (acaoPendente && acaoPendente.tipo !== "ativar") {
      toast.success(TOAST_POR_ACAO[acaoPendente.tipo]);
    }
    if (acaoPendente?.tipo === "editar") {
      const { uso } = acaoPendente.distribuicao;
      if (uso.escopos + uso.obras > 0) setAplicandoId(acaoPendente.distribuicao.id);
    }
    setAcaoPendente(null);
    dados.recarregar();
  };

  let dialogo: DistribuicoesViewModel["dialogo"] = null;
  if (acaoPendente?.tipo === "nova") {
    dialogo = { modo: "nova", onClose: fecharAcao, onConcluido: concluirAcao };
  } else if (acaoPendente?.tipo === "editar") {
    dialogo = {
      modo: "edicao",
      distribuicao: acaoPendente.distribuicao,
      onClose: fecharAcao,
      onConcluido: concluirAcao,
    };
  }

  let situacaoDialogo: DistribuicoesViewModel["situacaoDialogo"] = null;
  if (acaoPendente?.tipo === "inativar") {
    situacaoDialogo = {
      distribuicao: acaoPendente.distribuicao,
      onClose: fecharAcao,
      onConcluido: concluirAcao,
    };
  }

  return {
    conteudo,
    erro: dados.erro,
    mostrarInativas,
    alternarInativas: () => setMostrarInativas((atual) => !atual),
    nos,
    recarregar: dados.recarregar,
    nova: podeConsultar && podeEditar ? () => setAcaoPendente({ tipo: "nova" }) : undefined,
    dialogo,
    situacaoDialogo,
    aplicacaoDialogo: aplicandoId
      ? {
          tipo: "DISTRIBUICAO",
          referenciaId: aplicandoId,
          onClose: () => setAplicandoId(null),
        }
      : null,
    acaoPendente,
    fecharAcao,
    concluirAcao,
    mobile,
  };
}
